const listenerGuards = new WeakMap();
const INTERVAL_MS = 200;
const MAX_BATCH = 4;
const BUDGET_MS = 2;

function ancestor(el, component) {
  for (let node = el, depth = 0; node && depth < 32; node = node.parentNode, depth++) {
    if (node.components?.[component]) return node;
  }
  return null;
}

// Only the paused period and first eventual native update are guarded. Keep
// Three's normal native writer and caches; consume its idle clock gap BEFORE it
// schedules AudioParams, even if the first visible frame was itself delayed.
export function guardPausedListener(listener, host) {
  if (!listener?.updateMatrixWorld || !listener._clock?.getDelta || listenerGuards.has(listener)) return;
  const original = listener.updateMatrixWorld;
  const hadOwnMethod = Object.prototype.hasOwnProperty.call(listener, "updateMatrixWorld");
  const wrapper = function (...args) {
    this._clock.getDelta();
    const result = original.apply(this, args);
    this.timeDelta = 0.02;
    if (!host.document.hidden) {
      if (this.updateMatrixWorld === wrapper) {
        if (hadOwnMethod) this.updateMatrixWorld = original;
        else delete this.updateMatrixWorld;
      }
      listenerGuards.delete(listener);
    }
    return result;
  };
  listenerGuards.set(listener, wrapper);
  listener.updateMatrixWorld = wrapper;
}

function hasPose(component, object) {
  const info = component.bufferInfos?.find(value => value.object3D === object);
  if (!info?.componentNames.includes("position") || !info.componentNames.includes("rotation")) return false;
  const frame = info.buffer.buffer.length
    ? info.buffer.buffer[info.buffer.buffer.length - 1]
    : info.buffer.state !== 0 && info.buffer.originFrame;
  if (!frame) return false;
  return [
    frame.position?.x,
    frame.position?.y,
    frame.position?.z,
    frame.quaternion?.x,
    frame.quaternion?.y,
    frame.quaternion?.z,
    frame.quaternion?.w
  ].every(Number.isFinite);
}

export function refreshBackgroundAudio(entry, now, elapsed, advanceAvatar = true) {
  const { root, audio, el } = entry;
  const networked = root.components.networked;
  const camera = root.components["ik-root"]?.camera;
  if (!camera) return false;
  const useLerp = typeof NAF === "undefined" || NAF.options.useLerp !== false;
  if (useLerp && (!hasPose(networked, root.object3D) || !hasPose(networked, camera.object3D))) return false;
  // Only this remote avatar, never the scene's networking/animation/physics ticks.
  const ikEl = ancestor(el, "ik-controller");
  const ik = ikEl?.components["ik-controller"];
  if (advanceAvatar) {
    networked.tick(now, Math.min(1000, elapsed));
    networked.tick(now, 0); // Publish the interpolator's newly advanced sample.
    if (ik?.hasIKBones) {
      ik.ikCustomController?.adjustCamera();
      camera.object3D.matrixNeedsUpdate = true;
      camera.object3D.updateMatrices();
      ik.forceIkUpdate = true;
      ik.tick(now, Math.min(50, elapsed));
    }
  }
  audio.listener.updateMatrices();
  audio.listener.updateMatrixWorld();
  audio.updateMatrices();
  audio.updateMatrixWorld(true);
  return true;
}

export class BackgroundAudioUpdater {
  constructor(host, options = {}) {
    this.host = host;
    this.refresh = options.refresh || refreshBackgroundAudio;
    this.entries = new Map();
    this.byElement = new Map();
    this.timer = null;
    this.cursor = null;
    this.onVisibility = this.onVisibility.bind(this);
  }

  register(el, audio, onRefreshed) {
    const root = ancestor(el, "networked");
    if (root?.components.networked.data.template !== "#remote-avatar" || root.components.networked.isMine?.()) return;
    const old = this.byElement.get(el);
    if (old) this.unregister(old);
    const entry = {
      el,
      audio,
      root,
      onRefreshed,
      owner: root.components.networked.data.owner,
      networked: root.components.networked,
      room: this.host.APP?.hub?.hub_id,
      frame: this.host.APP?.world?.time?.tick,
      last: this.host.performance.now(),
      warned: false
    };
    this.entries.set(audio, entry);
    this.byElement.set(el, audio);
    if (this.entries.size === 1) this.host.document.addEventListener("visibilitychange", this.onVisibility);
    if (this.host.document.hidden) {
      this.update(entry);
      this.schedule(0);
    }
  }

  current(entry) {
    return (
      entry.el.parentNode &&
      entry.root.isConnected !== false &&
      entry.root.contains(entry.el) &&
      entry.el.getObject3D("avatar-audio-source") === entry.audio &&
      entry.root.components?.networked === entry.networked &&
      entry.networked.data.owner === entry.owner &&
      this.host.APP?.hub?.hub_id === entry.room
    );
  }

  update(entry, refreshedRoots) {
    if (!this.current(entry)) {
      this.unregister(entry.audio);
      return;
    }
    if (entry.el.sceneEl?.renderer?.xr?.isPresenting) return;
    const now = this.host.performance.now();
    guardPausedListener(entry.audio.listener, this.host);
    const frame = this.host.APP?.world?.time?.tick;
    if (frame !== undefined && entry.frame !== frame) {
      entry.frame = frame;
      entry.last = now;
      return;
    }
    try {
      if (this.refresh(entry, now, Math.max(0, now - entry.last), !refreshedRoots?.has(entry.root))) {
        refreshedRoots?.add(entry.root);
        entry.last = now;
        entry.onRefreshed();
      }
    } catch (error) {
      if (!entry.warned) {
        entry.warned = true;
        console.warn("Background voice update failed", error);
      }
    }
  }

  schedule(delay) {
    if (this.timer !== null || !this.host.document.hidden || !this.entries.size) return;
    this.timer = this.host.setTimeout(() => {
      this.timer = null;
      this.run();
    }, delay);
  }

  run() {
    if (!this.host.document.hidden) return;
    if (!this.cursor) {
      this.cursor = this.entries.values();
      this.remaining = this.entries.size;
    }
    const start = this.host.performance.now();
    let count = 0;
    const refreshedRoots = new Set();
    while (this.cursor && count < MAX_BATCH && this.host.performance.now() - start < BUDGET_MS) {
      const next = this.cursor.next();
      if (next.done) {
        this.cursor = null;
        break;
      }
      count++;
      this.remaining--;
      this.update(next.value, refreshedRoots);
      if (this.remaining === 0) this.cursor = null;
    }
    this.schedule(this.cursor ? 0 : INTERVAL_MS);
  }

  onVisibility() {
    if (this.host.document.hidden) {
      this.cursor = null;
      this.schedule(0);
      return;
    }
    if (this.timer !== null) this.host.clearTimeout(this.timer);
    this.timer = null;
    this.cursor = null;
    // The native guard remains until the actual first resumed listener update.
  }

  unregister(audio) {
    const entry = this.entries.get(audio);
    if (!entry) return;
    this.entries.delete(audio);
    if (this.byElement.get(entry.el) === audio) {
      this.byElement.delete(entry.el);
    }
    if (!this.entries.size) {
      if (this.timer !== null) this.host.clearTimeout(this.timer);
      this.timer = null;
      this.cursor = null;
      this.host.document.removeEventListener("visibilitychange", this.onVisibility);
    }
  }
}

let updater;
export function getBackgroundAudioUpdater() {
  if (!updater) updater = new BackgroundAudioUpdater(window);
  return updater;
}
